//src/pages/inPatientDetail/patientTreatments.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
} from "@mui/material";
import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useFormik } from "formik";
import dayjs from "dayjs";
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import { LARGE_FORM_MODAL_SX } from "../../utils/formModalStyles";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { create } from "../../services/PatientTreatmentService";
import { getAllStaff } from "../../services/StaffService";
import { getMedicineList } from "../../services/medicineService";
import { getAllMedicine } from "../../services/medicineNewService";
import { isIvFluidMedicineType } from "../../utils/medicinePricingUtils";

const treatmentTypes = ["Medicine", "IV Fluids"];

// Validation schema for main form fields
const validationSchema = Yup.object({
  treatmentDate: Yup.date().nullable().required("Treatment Date is required"),
  treatmentTime: Yup.date().nullable().required("Treatment Time is required"),
  remarks: Yup.string(),
});

const PatientTreatment = ({ open, onClose, admissionId, patientId, patient_info }) => {
  const [staffList, setStaffList] = useState([]);
  const [medicine_list, setMedicine] = useState([]);
  const [treatments, setTreatments] = useState([
    {
      treatmentType: "",
      medicine: "",
      dosage: "",
      nurseId: "",
    },
  ]);

  useEffect(() => {
    fetchAllStaff();
    getRawMedicine();
  }, []);

  useEffect(() => {
    if (patient_info?.treatments && patient_info.treatments.length > 0) {
      setTreatments(patient_info.treatments);
    }
  }, [patient_info]);

  const fetchAllStaff = async () => {
    try {
      const res = await getAllStaff();
      setStaffList(res);
    } catch (error) {
      console.error("Failed to fetch staff list", error);
    }
  };

  const getRawMedicine = async () => {
    const data = await getMedicineList();
    setMedicine(data);
  };

  const formik = useFormik({
    initialValues: {
      treatmentDate: patient_info?.treatmentDate
        ? dayjs(patient_info.treatmentDate)
        : null,
      treatmentTime: patient_info?.treatmentTime
        ? dayjs(patient_info.treatmentTime)
        : null,
      remarks: patient_info?.remarks || "",
    },
    enableReinitialize: true,
    validationSchema, 
    onSubmit: async (values) => {
      // Validate all treatment rows have required fields
      for (let i = 0; i < treatments.length; i++) {
        const t = treatments[i];
        if (!t.treatmentType || !t.medicine || !t.medicineId || !t.dosage || !t.nurseId) {
          alert(`Please fill all fields for treatment row ${i + 1}`);
          return;
        }
      }

      try {
        // Prepare payload for backend
        const payload = treatments.map((t) => ({
          patientAssignmentId: patientId,
          admissionId: admissionId,
          treatmentDate: values.treatmentDate?.format("YYYY-MM-DD"),
          treatmentTime: values.treatmentTime?.format("HH:mm"),
          remarks: values.remarks,
          treatmentType: t.treatmentType,
          medicine: t.medicine,
          medicineId: t.medicineId,
          dosage: t.dosage,
          performedBy: t.nurseId,
        }));

        // Assuming your create API supports multiple records in array, or you can batch send them
        await create(payload);
        alert("Treatment(s) submitted successfully!");
        handleCloseModalState();
      } catch (error) {
        console.error("Submit error:", error);
        alert("Failed to submit treatment.");
      }
    },
  });

  const handleAddTreatmentRow = () => {
    setTreatments([
      ...treatments,
      { treatmentType: "", medicine: "", medicineId: "", dosage: "", nurseId: "" },
    ]);
  };

  const handleTreatmentChange = (index, field, value) => {
    const updated = [...treatments];
    updated[index][field] = value;
    setTreatments(updated);
  };

  const handleRemoveTreatmentRow = (index) => {
    if (treatments.length === 1) return; // prevent removing last row
    const updated = [...treatments];
    updated.splice(index, 1);
    setTreatments(updated);
  };

  const handleCloseModalState = () => {
    onClose();
    formik.resetForm();
    setTreatments([
      {
        treatmentType: "",
        medicine: "",
        dosage: "",
        nurseId: "",
      },
    ]);
  };

  const getFilteredMedicines = (type) => {
    if (!type) return [];

    if (type === "Medicine") {
      // For Medicine, show all medicines EXCEPT IV fluids
      return medicine_list.filter((m) => !isIvFluidMedicineType(m.medicineType));
    }

    if (type === "IV Fluids") {
      // For IV Fluids, show only IV fluid medicines
      return medicine_list.filter((m) => isIvFluidMedicineType(m.medicineType));
    }

    return [];
  };

  return (
    <BasicModal
      open={open}
      onClose={handleCloseModalState}
      sx={LARGE_FORM_MODAL_SX}
    >
      <Box display={"flex"} flexDirection={"column"}>
        <Box display={"flex"} justifyContent={"center"} alignItems={"center"}>
          <Typography
            variant="h4"
            sx={{ fontSize: "1.5rem", fontWeight: "bold", textAlign: "center", pb: 1.5 }}
          >
            Patient Treatments and Medicines
          </Typography>
        </Box>
        <Box>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              {/* Treatment Date and Time */}
              <Grid item xs={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Treatment Date"
                    format="YYYY-MM-DD"
                    value={formik.values.treatmentDate}
                    onChange={(value) =>
                      formik.setFieldValue("treatmentDate", value)
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error:
                          formik.touched.treatmentDate &&
                          Boolean(formik.errors.treatmentDate),
                        helperText:
                          formik.touched.treatmentDate &&
                          formik.errors.treatmentDate,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    label="Treatment Time"
                    ampm
                    value={formik.values.treatmentTime}
                    onChange={(value) =>
                      formik.setFieldValue("treatmentTime", value)
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error:
                          formik.touched.treatmentTime &&
                          Boolean(formik.errors.treatmentTime),
                        helperText:
                          formik.touched.treatmentTime &&
                          formik.errors.treatmentTime,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Remarks */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="remarks"
                  name="remarks"
                  label="Remarks"
                  value={formik.values.remarks}
                  onChange={formik.handleChange}
                  error={formik.touched.remarks && Boolean(formik.errors.remarks)}
                  helperText={formik.touched.remarks && formik.errors.remarks}
                />
              </Grid>

              {/* Treatments header */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Treatments
                </Typography>
              </Grid>

              {/* Treatments rows */}
              {treatments.map((row, index) => (
                  <Grid
                    container
                    spacing={1}
                    alignItems="center"
                    key={index}
                    mb={1}
                  >

                  <Grid item xs={3}>
                    <TextField
                      select
                      fullWidth
                      label="Treatment Type"
                      value={row.treatmentType}
                      onChange={(e) =>
                        handleTreatmentChange(index, "treatmentType", e.target.value)
                      }
                      size="small"
                    >
                      {treatmentTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={3}>
                    <TextField
                      select
                      fullWidth
                      label="Medicine"
                      value={row.medicine}
                      onChange={(e) => {
                        const selectedMedicineName = e.target.value;
                        const selectedMedicine = medicine_list.find(med => med.name === selectedMedicineName);
                        handleTreatmentChange(index, "medicine", selectedMedicineName);
                        handleTreatmentChange(index, "medicineId", selectedMedicine ? selectedMedicine.medicineId : "");
                      }}
                      size="small"
                    >
                      {(() => {
                        let filteredMedicines = [];
                        
                        if (row.treatmentType === "Medicine") {
                          // For Medicine, show all medicines EXCEPT IV fluids
                          filteredMedicines = medicine_list.filter((m) => !isIvFluidMedicineType(m.medicineType));
                        } else if (row.treatmentType === "IV Fluids") {
                          // For IV Fluids, show only IV fluid medicines
                          filteredMedicines = medicine_list.filter((m) => isIvFluidMedicineType(m.medicineType));
                        }
                        // For other treatment types, show no medicines
                        
                        return filteredMedicines.map((med) => (
                          <MenuItem key={med.id} value={med.name}>
                            {med.name}
                          </MenuItem>
                        ));
                      })()}
                    </TextField>
                  </Grid>

                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      label="Dosage"
                      value={row.dosage}
                      onChange={(e) =>
                        handleTreatmentChange(index, "dosage", e.target.value)
                      }
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <TextField
                      select
                      fullWidth
                      label="Performed By"
                      value={row.nurseId}
                      onChange={(e) =>
                        handleTreatmentChange(index, "nurseId", e.target.value)
                      }
                      size="small"
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            style: {
                              maxHeight: 200,
                              overflowY: "auto",
                            },
                          },
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                          },
                          getContentAnchorEl: null,
                        },
                      }}
                    >
                      {staffList
                        .filter((staff) => staff.userRole === "staff")
                        .map((staff) => (
                          <MenuItem key={staff.id} value={staff.id}>
                            {staff.name}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={1} display="flex" justifyContent="center">
                    {treatments.length > 1 && (
                      <Button
                        color="error"
                        variant="outlined"
                        onClick={() => handleRemoveTreatmentRow(index)}
                        size="small"
                        sx={{ minWidth: "30px", padding: "4px" }}
                      >
                        X
                      </Button>
                    )}
                  </Grid>
                </Grid>
              ))}

              {/* Add More button right aligned and small */}
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={handleAddTreatmentRow}
                  size="small"
                  sx={{ minWidth: "100px" }}
                >
                  + Add More
                </Button>
              </Grid>

              {/* Submit button */}
              <Grid item xs={12}>
                <FormModalActions
                  onCancel={handleCloseModalState}
                  submitType="submit"
                  submitDisabled={formik.isSubmitting}
                  loading={formik.isSubmitting}
                />
              </Grid>
            </Grid>
          </form>
        </Box>
      </Box>
    </BasicModal>
  );
};

export default PatientTreatment;